import { ChipProps } from '@c2fo/liquidity';
import { PlayableInvoiceResponse } from '@/enterprise/data/usePlayableInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { EligibleInvoiceFilterOptions } from './EligibleInvoiceFilters';

export interface EligibleInvoice {
  voucherId: string;
  amount: number;
  dueDate: string;
  payDate: string;
  daysPaidEarly: number;
  goodFundsDate: string;
  status: 'eligible' | 'excluded' | 'inAnOffer' | 'closedMarket';
  currency: string;
  originalAmount: number;
  adjustmentAmount: number | null;
  coversUnmatched: boolean;
}

export type PlayableInvoice = PlayableInvoiceResponse['data']['invoices'][number];

export const mapPlayableInvoicesToEligibleInvoices = ({
  invoices,
  takerMarkets,
}: {
  invoices: PlayableInvoice[];
  takerMarkets: TakerMarket[];
}) => {
  return invoices.map((invoice) => {
    const matchingTakerMarket = takerMarkets.find(
      (tm) => tm.marketUuid === invoice.marketUuid && tm.takerDivisionUuid === invoice.supplierDivisionUuid
    );

    return {
      voucherId: invoice.voucherId,
      amount: invoice.amount,
      dueDate: invoice.originalDepositDate || '',
      payDate: matchingTakerMarket?.marketPayDate || '',
      daysPaidEarly: invoice.daysPaidEarly,
      goodFundsDate: matchingTakerMarket?.marketGoodFundsDate || '',
      status: invoice.supplierExcluded
        ? 'excluded'
        : !matchingTakerMarket?.marketIsEnabled
        ? 'closedMarket'
        : matchingTakerMarket?.offerConfig.isEnabled
        ? 'inAnOffer'
        : 'eligible',
      currency: invoice.currency,
      originalAmount: invoice.originalAmount,
      adjustmentAmount: invoice.adjustmentAmount,
      coversUnmatched: invoice.coversUnmatched,
    } satisfies EligibleInvoice;
  });
};

export const statusChipConfig: Record<
  EligibleInvoice['status'],
  { labelKey: string; type: ChipProps['type']; tooltipKey: string }
> = {
  eligible: {
    labelKey: 'core.eligible',
    type: 'primary',
    tooltipKey: 'invoiceCentral.eligibleTooltip',
  },
  inAnOffer: {
    labelKey: 'invoiceCentral.inAnOffer',
    type: 'info',
    tooltipKey: 'invoiceCentral.inAnOfferTooltip',
  },
  excluded: {
    labelKey: 'core.excluded',
    type: 'default',
    tooltipKey: 'invoiceCentral.excludedTooltip',
  },
  closedMarket: {
    labelKey: 'invoiceCentral.closedMarket',
    type: 'warning',
    tooltipKey: 'invoiceCentral.closedMarketTooltip',
  },
};

export const mergeFilters = ({
  prevFilters,
  newFilters,
}: {
  prevFilters?: EligibleInvoiceFilterOptions;
  newFilters?: EligibleInvoiceFilterOptions;
}) => {
  if (!newFilters) {
    return prevFilters ?? {};
  }

  let mergedFilters = prevFilters ?? {};
  // Handle merging division (takerMakerId) filter
  if (newFilters?.takerMakerId !== undefined) {
    const { takerMakerId: _, ...rest } = mergedFilters ?? {};
    mergedFilters = {
      ...rest,
      ...(newFilters.takerMakerId !== null && { takerMakerId: newFilters.takerMakerId }),
    };
  }

  // Helper function to merge filters with range values (from/to)
  const mergeRangeFilter = <K extends Exclude<keyof EligibleInvoiceFilterOptions, 'takerMakerId'>>(filterKey: K) => {
    if (!newFilters[filterKey]) {
      return;
    }

    // Date filters from/to properties are strings, but amount is number
    // Create type parameter for the filter type in this call
    type FilterType = EligibleInvoiceFilterOptions[K];
    const newFilter = newFilters[filterKey] as FilterType;
    const { [filterKey]: prevFilter, ...rest } = mergedFilters;

    let mergedFilter = newFilter;
    if (prevFilter) {
      mergedFilter = {
        ...(newFilter?.from !== null && { from: newFilter?.from ?? prevFilter.from }),
        ...(newFilter?.to !== null && { to: newFilter?.to ?? prevFilter.to }),
      } as FilterType;
    }

    mergedFilters = {
      ...rest,
      ...((mergedFilter?.from || mergedFilter?.to) && { [filterKey]: mergedFilter }),
    };
  };

  mergeRangeFilter('originalDueDate');
  mergeRangeFilter('paymentSent');
  mergeRangeFilter('paymentDeposit');
  mergeRangeFilter('amount');

  return mergedFilters;
};
